import { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { XMarkIcon, ArrowUpTrayIcon } from '@heroicons/react/24/outline';
import { useData } from '../context/DataContext.js';

export default function AddLayout({ isOpen, onClose, onLayoutAdded, onLayoutSelected, venueId, venueName }) {
  const { layouts, refreshLayouts } = useData();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [layoutName, setLayoutName] = useState('');
  const [existingLayout, setExistingLayout] = useState(null);

  // Reset form when modal opens
  useEffect(() => {
    if (isOpen) {
      setLayoutName('');
      setError('');
      setExistingLayout(null);
    }
  }, [isOpen]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);

    try {
      // Check if layout exists
      const existingLayout = layouts.find(
        l => l.LayoutName.toLowerCase() === layoutName.toLowerCase().trim() 
        && parseInt(l.VenueID) === parseInt(venueId)
      );
      
      if (existingLayout) {
        setExistingLayout(existingLayout);
        setError('A layout with this name already exists for this venue.');
        setIsLoading(false);
        return;
      }

      const response = await fetch(
        'https://r7ej0e9i28.execute-api.ap-southeast-2.amazonaws.com/dev/layouts',
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            LayoutName: layoutName,
            VenueID: venueId
          })
        }
      );

      if (!response.ok) {
        throw new Error('Failed to create layout');
      }

      await refreshLayouts();
      if (onLayoutAdded) {
        onLayoutAdded();
      }
      onClose();
    } catch (err) {
      console.error('Error creating layout:', err);
      setError('Failed to create layout. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  // Add function to use existing layout
  const handleUseExisting = () => {
    if (onLayoutSelected) {
      onLayoutSelected(existingLayout);
    }
    if (onLayoutAdded) {
      onLayoutAdded();
    }
    onClose();
  };

  if (!isOpen) return null;
  if (!venueId) {
    return createPortal(
      <div className="fixed inset-0 z-[230] flex items-center justify-center bg-gray-500 bg-opacity-75 p-4">
        <div className="w-full max-w-md rounded-lg bg-white p-6 shadow-xl">
          <p className="text-center text-gray-700">Please select a venue first</p>
          <button
            type="button"
            onClick={onClose}
            className="mt-4 w-full rounded-md bg-slate-900 px-3 py-2 text-sm font-semibold text-white hover:bg-slate-700"
          >
            Close
          </button>
        </div>
      </div>,
      document.body
    );
  }

  return createPortal(
    <div className="fixed inset-0 z-[230] flex items-center justify-center bg-gray-500 bg-opacity-75 p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-md">
        <div className="flex items-center justify-between px-4 py-4 border-b">
          <h2 className="text-xl font-semibold text-gray-900">Add New Layout</h2>
          <div className="flex items-center space-x-2">
            <button
              type="button"
              disabled={isLoading}
              onClick={handleSubmit}
              className="flex flex-col items-center px-2 text-slate-900 hover:text-slate-700 disabled:text-slate-400"
            >
              <ArrowUpTrayIcon className="h-6 w-6" />
              <span className="text-[10px]">Save</span>
            </button>
            <button
              onClick={onClose}
              className="flex flex-col items-center px-2 text-gray-500 hover:text-gray-700"
            >
              <XMarkIcon className="h-6 w-6" />
              <span className="text-[10px]">Close</span>
            </button>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="p-6 bg-gray-50">
          {error && existingLayout ? (
            <div className="mb-4 space-y-4">
              <div className="text-sm text-red-600 text-center">
                {error}
              </div>
              <div className="bg-gray-50 p-4 rounded-md">
                <h3 className="font-medium text-gray-900">Existing Layout Details:</h3>
                <p className="text-sm text-gray-600 mt-1">
                  {existingLayout.LayoutName}<br />
                  for {venueName}
                </p>
                <button
                  type="button"
                  onClick={handleUseExisting}
                  className="mt-3 w-full rounded-md bg-slate-900 px-3 py-1.5 text-sm font-semibold text-white hover:bg-slate-700"
                >
                  Use Existing Layout
                </button>
              </div>
            </div>
          ) : error ? (
            <div className="mb-4 text-sm text-red-600 text-center">
              {error}
            </div>
          ) : null}

          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700">
                Venue
              </label>
              <p className="mt-1 text-sm text-gray-600">{venueName}</p>
            </div>

            <div>
              <label htmlFor="layoutName" className="block text-sm font-medium text-gray-700">
                Layout Name *
              </label>
              <input
                type="text"
                id="layoutName"
                required
                value={layoutName}
                onChange={(e) => setLayoutName(e.target.value)}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-slate-500 focus:ring-slate-500"
              />
            </div>
          </div>
        </form>
      </div>
    </div>,
    document.body
  );
} 